import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import { MapPin, Menu, Mic, MoveUpRight } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

// Styled components for the login form
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const LoginModal = styled.div`
  width: 647px;
  background: #000000;
  border-radius: 25px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  @media (max-width: 768px) {
    width: 90%;
    min-height: 450px;
    padding: 24px;
  }
  
  @media (max-width: 480px) {
    width: 95%;
    min-height: 400px;
    padding: 20px;
  }
`;

const LoginHeader = styled.div`
  margin-bottom: 24px;
  
  h2 {
    color: #ffffff;
    font-size: 25px;
    font-weight: 600;
    margin: 0;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
    
    h2 {
      font-size: 22px;
    }
  }
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 460px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    color: #ffffff;
    font-size: 16px;
    margin-bottom: 6px;
    font-weight: 500;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    
    label {
      font-size: 14px;
      margin-bottom: 5px;
    }
  }
`;

const FormInput = styled.input`
  width: 100%;
  height: 46px;
  background: rgba(217, 217, 217, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 14px;
    padding: 0 16px;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  height: 48px;
  background: #F43F60;
  border: none;
  border-radius: 12px;
  color: #EDEDED;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-top: 8px;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 16px;
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: 460px;
  text-align: center;
  margin: 20px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  span {
    background: #000000;
    color: #9E9E9E;
    padding: 0 20px;
    font-size: 16px;
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: 480px) {
    margin: 16px 0;
    
    span {
      font-size: 14px;
      padding: 0 16px;
    }
  }
`;

const SocialLogin = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const SocialButton = styled.button`
  width: 100px;
  height: 53px;
  background: #363636;
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #404040;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }

   @media (max-width: 768px) {
    width: auto;
    height: 50px;
    padding: 0 16px;
  }
`;

const SignupLink = styled.div`
  text-align: center;
  margin-top: 16px;
  
  span {
    color: #D4D4D4;
    font-size: 16px;
    margin-right: 10px;
  }
  
  .create-account {
    color: #F43F60;
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 480px) {
    margin-top: 12px;
    
    span, .create-account {
      font-size: 14px;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;




const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  // Login form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [socket, setSocket] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [currentOffer, setCurrentOffer] = useState(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };
  const PUBLIC_VAPID_KEY = "BPBn54Pl2n5iaPnsJB98M63GjwfPtoT6TFNnjzEVo54vJguH7rvboECJXSjTskJhm1Fy8iuv7Vv70pwzpSm8qCA";
  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");
  
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }
  const getSubscription = async () => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js");
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
        });

        return subscription;
      } catch (err) {
        console.log(err);
        console.error("Subscription failed:", err);
        return null;
      }
    }
    return null;
  };
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const subscription = await getSubscription();
      console.log(subscription);
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/user/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          webPushSubscription: subscription
        })
      });

      const data = await response.json();

      console.log(data);
      if (response.ok) {
        setSuccess('Login successful!');
        // Store token in sessionStorage
        sessionStorage.setItem('token', data.data.token);
        sessionStorage.setItem('user', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = redirect || '/user/dashboard'; // or wherever you want to redirect
        }, 1500);
      } else {
        setError(data.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // OAuth handlers
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsOAuthLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: credentialResponse.credential,
          userInfo: {}
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setSuccess('Google login successful!');
        // Store token in sessionStorage
        sessionStorage.setItem('token', data.data.token);
        sessionStorage.setItem('user', JSON.stringify(data.data.user));

        // Redirect to dashboard or home page
        setTimeout(() => {
          window.location.href = redirect || '/user/dashboard';
        }, 1500);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message || 'Google login failed. Please try again.');
    } finally {
      setIsOAuthLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google login error:', error);
    setError('Google login failed. Please try again.');
  };



  // --- UI ---
  return (
    <>
      {/* Loader */}
      {/* <div className="loaders">
        <div className="loader"></div>
      </div> */}
      {/* Header - custom class names to avoid CSS conflicts */}

      {/* Main Content */}
      <main className="main-content">
        <section className="search-area home" style={{ backgroundImage: 'url(/home-bg.png)', backgroundSize: 'cover', backgroundPosition: 'top', backgroundRepeat: 'no-repeat' }}>
          <div className="containers">
            <div className="row justify-content-center" >
              <div className="col-md-12 col-lg-10 col-xl-10" >

                {/* Login Form */}
                <LoginContainer>
                  <LoginModal>
                    <LoginHeader>
                      <h2>User Login</h2>
                    </LoginHeader>

                    <LoginForm onSubmit={handleSubmit}>
                      {error && (
                        <ErrorMessage>
                          {error}
                        </ErrorMessage>
                      )}

                      {success && (
                        <SuccessMessage>
                          {success}
                        </SuccessMessage>
                      )}



                      <FormGroup>
                        <label htmlFor="email">Email</label>
                        <FormInput
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          required
                        />
                      </FormGroup>

                      <FormGroup>
                        <label htmlFor="password">Password</label>
                        <FormInput
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                        />
                      </FormGroup>

                      <LoginButton type="submit" disabled={isLoading || isOAuthLoading}>
                        {isLoading ? 'Logging in...' : 'Log in'}
                      </LoginButton>
                    </LoginForm>

                    <Divider>
                      <span>Or</span>
                    </Divider>

                    <SocialLogin>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        width="115"
                        text="signin_with"
                        shape="rectangular"
                        logo_alignment="left"
                        disabled={isOAuthLoading}
                      />

                    </SocialLogin>

                    <SignupLink>
                      <span>Don't have an account?</span>
                      <Link to="/user/signup" className="create-account">Create account</Link>
                    </SignupLink>
                  </LoginModal>
                </LoginContainer>
              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="footer-area">
        <div className="containers">
          <div className="row">
            <div className="col-md-6">
              <div className="copy">
                <p>Copyright © 2024 Automotiveai.com</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="trams">
                <p><a href="#">Privacy Policy</a> | <a href="#">Terms & Conditions</a></p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <a href="#" className="scrolltotop"><i className="bi bi-arrow-up-circle"></i></a>
        </div>
      </footer>
    </>
  );
};

export default Login; 